import React from 'react';
import { Alert, Box, Link, Typography } from '@mui/material';
import LeadContactValues from '../../components/Common/LeadContactValues';

const FIELD_ROWS = [
  ['Lead Emails', 'email'],
  ['Lead Phone Numbers', 'phone'],
  ['Lead LinkedIn Profiles', 'linkedinUrl'],
  ['Lead Names', 'name'],
  ['Lead Titles', 'title'],
  ['Confidence', 'confidence'],
  ['Source', 'source'],
];

function fieldValue(contact, key) {
  const value = contact?.[key];
  return value === undefined || value === null ? '' : String(value).trim();
}

function LeadField({ label, value, href = null, contact = null, channel = null }) {
  if (!value) return null;
  return (
    <Box display="grid" gridTemplateColumns={{ xs: '1fr', sm: '150px 1fr' }} gap={0.75}>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="caption" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
        {channel ? <LeadContactValues value={value} contacts={[contact]} channel={channel} /> : href ? (
          <Link href={href} target="_blank" rel="noreferrer" underline="hover">
            {value}
          </Link>
        ) : (
          value
        )}
      </Typography>
    </Box>
  );
}

function emptyContactCopy(count) {
  if (!count) return null;
  return `No lead contacts were returned for ${count} previewed ${count === 1 ? 'company' : 'companies'}.`;
}

export default function ChatArtifactLeadSummary({ summary = null }) {
  if (!summary || summary.enrichedCompanyCount <= 0) return null;

  const companies = Array.isArray(summary.companies) ? summary.companies : [];
  const noContactCopy = emptyContactCopy(summary.noContactCompanyCount || 0);

  return (
    <Box
      sx={{
        pt: 1.25,
        borderTop: '1px solid',
        borderTopColor: 'divider',
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, letterSpacing: 0 }}>
        Lead contacts
      </Typography>
      <Box display="flex" flexDirection="column" gap={1}>
        {companies.map((company) => (
          <Box
            key={company.companyId || company.companyName}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '8px',
              bgcolor: 'background.paper',
              p: 1.25,
            }}
          >
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 1 }}>
              {company.companyName || 'Company'}
            </Typography>
            <Box display="flex" flexDirection="column" gap={1.25}>
              {(company.contacts || []).map((contact, index) => (
                <Box
                  key={`${company.companyId || company.companyName}-${index}`}
                  display="flex"
                  flexDirection="column"
                  gap={0.5}
                >
                  {FIELD_ROWS.map(([label, key]) => (
                    <LeadField
                      key={key}
                      label={label}
                      value={fieldValue(contact, key)}
                      href={key === 'linkedinUrl' ? fieldValue(contact, key) : null}
                      contact={contact}
                      channel={{email: 'email', phone: 'phone', linkedinUrl: 'linkedin'}[key]}
                    />
                  ))}
                  {Array.isArray(contact.evidence) && contact.evidence.length > 0 && (
                    <LeadField label="Evidence" value={contact.evidence.join('; ')} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>
        ))}
      </Box>
      {noContactCopy && (
        <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
          {noContactCopy}
        </Alert>
      )}
    </Box>
  );
}
